import { useCallback, useMemo, useRef, useState } from "react";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { formatDistanceToNow } from "date-fns";
import {
  Edit2,
  ExternalLink,
  Link as LinkIcon,
  MoreHorizontal,
  Plus,
  RefreshCw,
  ShieldCheck,
  Trash2,
} from "lucide-react";
import { useTranslation } from "react-i18next";
import {
  ActionMenu,
  Badge,
  Button,
  DataTable,
  PageContent,
  PageHeader,
  PageShell,
  StatusBadge,
  TableIdentity,
  localTablePage,
  updateTableState,
  type DataTableColumn,
  type DataTableState,
} from "@h2j-data-team/ui";

import type { QlikAuthMethod, QlikConnection, QlikConnectionStatus } from "@repo/shared";
import { ConfirmDialog, useConfirmDialog } from "@/components/shared";
import { DensityToggle } from "@/components/density-toggle";
import { FacetFilter } from "@/components/app/facet-filter";
import {
  connectionsQueryOptions,
  useDeleteConnection,
  useRefreshAllAppsMetadata,
  useTestConnection,
} from "@/features/qlik-connections/api";
import {
  CreateConnectionDrawer,
  EditConnectionDrawer,
} from "@/features/qlik-connections/components/connection-form-drawer";
import { listRefreshing, paginationLabels, tableMessages, tableSearch } from "@/lib/ui-labels";
import { useLocale } from "@/hooks/use-locale";
import { useSearchHotkey } from "@/lib/use-search-hotkey";
import { useTableDensity } from "@/lib/use-table-density";

const ALL_METHODS: QlikAuthMethod[] = ["api_key", "oauth_client_credentials"];
const PAGE_SIZES = [10, 25, 50, 100];

type ConnectionFilters = { methods: QlikAuthMethod[] };

const STATUS_TONE = {
  ok: "success",
  degraded: "warning",
  unknown: "info",
} as const satisfies Record<QlikConnectionStatus, "success" | "warning" | "info">;

export const Route = createFileRoute("/_authenticated/connections")({
  staticData: { pageTitle: "breadcrumbs.connections" },
  validateSearch: (search: Record<string, unknown>) => ({
    q: typeof search.q === "string" ? search.q : undefined,
  }),
  component: ConnectionsPage,
});

function ConnectionsPage() {
  const { q } = Route.useSearch();
  const navigate = useNavigate({ from: Route.fullPath });
  const { t } = useTranslation("connections");
  const { density, setDensity } = useTableDensity();
  const { dateFnsLocale } = useLocale();
  const connectionsQuery = useQuery(connectionsQueryOptions);
  const connections = connectionsQuery.data;
  const deleteMutation = useDeleteConnection();
  const testMutation = useTestConnection();
  const refreshAllAppsMutation = useRefreshAllAppsMetadata();
  const { confirm, dialogProps } = useConfirmDialog();
  const [refreshingAll, setRefreshingAll] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);
  const [editId, setEditId] = useState<string | null>(null);
  // The drawers open from a button or a row's menu: closing puts the focus back there.
  const openedFrom = useRef<HTMLElement | null>(null);
  const searchRef = useRef<HTMLInputElement>(null);
  useSearchHotkey(searchRef);

  // The search lives in the URL (shareable, survives a reload); page, size, sort
  // and the method filter are the table's own.
  const [local, setLocal] = useState<DataTableState<ConnectionFilters>>({
    page: 1,
    pageSize: 50,
    query: "",
    filters: { methods: [] },
    selection: [],
  });
  const state: DataTableState<ConnectionFilters> = { ...local, query: q ?? "" };

  const onStateChange = useCallback(
    (next: DataTableState<ConnectionFilters>) => {
      setLocal(next);
      if (next.query !== (q ?? "")) {
        navigate({ search: (prev) => ({ ...prev, q: next.query || undefined }), replace: true });
      }
    },
    [navigate, q],
  );

  const handleDelete = (connection: QlikConnection, trigger: HTMLElement | null) => {
    confirm({
      restoreFocus: trigger,
      title: t("delete.title"),
      description: t("delete.description", { name: connection.name }),
      confirmLabel: t("delete.confirmLabel"),
      variant: "destructive",
      onConfirm: async () => {
        await deleteMutation.mutateAsync(connection.id);
      },
    });
  };

  const handleRefreshAll = useCallback(async () => {
    if (!connections || refreshingAll) return;
    setRefreshingAll(true);
    try {
      for (const c of connections) {
        await refreshAllAppsMutation.mutateAsync(c.id).catch(() => undefined);
      }
    } finally {
      setRefreshingAll(false);
    }
  }, [connections, refreshAllAppsMutation, refreshingAll]);

  const authLabel = useCallback(
    (method: QlikAuthMethod) =>
      method === "oauth_client_credentials" ? t("methodOptions.oauth") : t("methodOptions.apiKey"),
    [t],
  );

  const columns = useMemo<DataTableColumn<QlikConnection>[]>(
    () => [
      {
        id: "name",
        header: t("table.headers.tenant"),
        variant: "identity",
        rowHeader: true,
        sort: "text",
        value: (c) => c.name,
        cell: (c) => <TableIdentity name={c.name} leading={<LinkIcon />} />,
      },
      {
        id: "url",
        header: t("table.headers.url"),
        sort: "text",
        value: (c) => c.baseUrl,
        cell: (c) => (
          <a
            href={c.baseUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex max-w-full items-center gap-1.5 font-mono text-xs text-dim hover:text-brand-text"
          >
            <span className="truncate">{stripUrlScheme(c.baseUrl)}</span>
            <ExternalLink className="h-3 w-3 shrink-0" aria-hidden />
            <span className="sr-only">{t("table.opensInNewTab")}</span>
          </a>
        ),
      },
      {
        id: "apps",
        header: t("table.headers.apps"),
        width: "6rem",
        align: "end",
        variant: "numeric",
        sort: "number",
        value: (c) => c.appCount ?? 0,
        cell: (c) => c.appCount ?? 0,
      },
      {
        id: "auth",
        header: t("table.headers.auth"),
        width: "8rem",
        sort: "text",
        value: (c) => authLabel(c.authMethod),
        cell: (c) => (
          <Badge variant="secondary" className="font-mono">
            {authLabel(c.authMethod)}
          </Badge>
        ),
      },
      {
        id: "lastSync",
        header: t("table.headers.lastSync"),
        width: "9rem",
        sort: "date",
        value: (c) => (c.lastSyncAt ? new Date(c.lastSyncAt) : null),
        cell: (c) =>
          c.lastSyncAt
            ? formatDistanceToNow(new Date(c.lastSyncAt), {
                addSuffix: true,
                locale: dateFnsLocale,
              })
            : t("table.lastSync.never"),
      },
      {
        id: "status",
        header: t("table.headers.status"),
        width: "9rem",
        sort: "text",
        value: (c) => t(`status.${c.status ?? "unknown"}`),
        cell: (c) => {
          const status: QlikConnectionStatus = c.status ?? "unknown";
          return <StatusBadge tone={STATUS_TONE[status]}>{t(`status.${status}`)}</StatusBadge>;
        },
      },
    ],
    [authLabel, dateFnsLocale, t],
  );

  const page = localTablePage(connections ?? [], state, {
    columns,
    // Name, URL and the raw auth method, as before the migration.
    search: (connection) => `${connection.name} ${connection.baseUrl} ${connection.authMethod}`,
    filter: (row, filters) =>
      filters.methods.length === 0 || filters.methods.includes(row.authMethod),
  });

  const clearFilters = () => {
    onStateChange(updateTableState(state, { query: "", filters: { methods: [] } }));
  };

  return (
    <PageShell
      header={
        <PageHeader
          title={t("list.pageTitle")}
          description={t("list.description")}
          actions={
            <>
              <Button
                variant="outline"
                size="sm"
                loading={refreshingAll}
                disabled={!connections || connections.length === 0}
                onClick={() => void handleRefreshAll()}
              >
                <RefreshCw />
                {t("toolbar.refreshAll")}
              </Button>
              <Button
                size="sm"
                onClick={(event) => {
                  openedFrom.current = event.currentTarget;
                  setCreateOpen(true);
                }}
              >
                <Plus />
                {t("list.addConnection")}
              </Button>
            </>
          }
        />
      }
    >
      <PageContent fill>
        <DataTable
          label={t("list.pageTitle")}
          columns={columns}
          rows={page.rows}
          getRowId={(c) => c.id}
          state={state}
          onStateChange={onStateChange}
          density={density}
          loading={connectionsQuery.isPending}
          refreshing={listRefreshing(connectionsQuery)}
          error={connectionsQuery.isError ? { title: t("list.loadFailed") } : null}
          onRetry={() => void connectionsQuery.refetch()}
          search={{
            ...tableSearch(t("toolbar.searchPlaceholder")),
            inputRef: searchRef,
            shortcutHint: "/",
            debounceMs: 300,
          }}
          toolbar={
            <>
              <FacetFilter
                label={t("toolbar.method")}
                options={ALL_METHODS.map((m) => ({ value: m, label: authLabel(m) }))}
                value={state.filters.methods}
                onValueChange={(methods) =>
                  onStateChange(
                    updateTableState(state, { filters: { methods: methods as QlikAuthMethod[] } }),
                  )
                }
              />
              <DensityToggle value={density} onChange={setDensity} />
            </>
          }
          pagination={{
            kind: "offset",
            total: page.total,
            pageSizeOptions: PAGE_SIZES,
            labels: paginationLabels(),
          }}
          rowActions={{
            label: t("table.actions"),
            width: 56,
            render: (connection) => (
              <ConnectionActions
                connection={connection}
                isTesting={testMutation.isPending && testMutation.variables === connection.id}
                onTest={() => testMutation.mutate(connection.id)}
                onEdit={(trigger) => {
                  openedFrom.current = trigger;
                  setEditId(connection.id);
                }}
                onDelete={(trigger) => handleDelete(connection, trigger)}
              />
            ),
          }}
          messages={tableMessages({
            empty: {
              title: t("list.empty.title"),
              description: t("list.empty.description"),
              action: (
                <Button
                  onClick={(event) => {
                    openedFrom.current = event.currentTarget;
                    setCreateOpen(true);
                  }}
                >
                  <Plus />
                  {t("list.empty.action")}
                </Button>
              ),
            },
            noResults: {
              title: t("list.noMatch.title"),
              description: t("list.noMatch.description"),
              action: (
                <Button variant="outline" size="sm" onClick={clearFilters}>
                  {t("list.noMatch.clearFilters")}
                </Button>
              ),
            },
          })}
        />
      </PageContent>

      <CreateConnectionDrawer
        open={createOpen}
        onOpenChange={setCreateOpen}
        restoreFocusRef={openedFrom}
      />
      <EditConnectionDrawer
        open={editId !== null}
        connectionId={editId ?? undefined}
        onOpenChange={(o) => {
          if (!o) setEditId(null);
        }}
        restoreFocusRef={openedFrom}
      />

      <ConfirmDialog {...dialogProps} isPending={deleteMutation.isPending} />
    </PageShell>
  );
}

function ConnectionActions({
  connection,
  isTesting,
  onTest,
  onEdit,
  onDelete,
}: {
  connection: QlikConnection;
  isTesting: boolean;
  onTest: () => void;
  /** Receives the row's menu button, where the drawer returns the focus on close. */
  onEdit: (trigger: HTMLElement | null) => void;
  /** Receives the row's menu button, where the dialog returns the focus on close. */
  onDelete: (trigger: HTMLElement | null) => void;
}) {
  const { t } = useTranslation("connections");
  const trigger = useRef<HTMLButtonElement>(null);
  const label = t("row.actionsLabel", { name: connection.name });

  return (
    <ActionMenu
      label={label}
      trigger={
        <Button ref={trigger} variant="ghost" size="icon-sm" aria-label={label}>
          <MoreHorizontal />
        </Button>
      }
      items={[
        {
          id: "test",
          label: t("row.actions.test"),
          icon: <ShieldCheck />,
          disabled: isTesting,
          onSelect: onTest,
        },
        {
          id: "edit",
          label: t("row.actions.edit"),
          icon: <Edit2 />,
          onSelect: () => onEdit(trigger.current),
        },
        { type: "separator", id: "sep" },
        {
          id: "delete",
          label: t("row.actions.delete"),
          icon: <Trash2 />,
          destructive: true,
          onSelect: () => onDelete(trigger.current),
        },
      ]}
    />
  );
}

function stripUrlScheme(url: string): string {
  return url.replace(/^https?:\/\//, "").replace(/\/$/, "");
}
